import { Component, input } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FieldsetModule } from 'primeng/fieldset';

import { ProgramacionPendiente } from 'src/app/domain/model/cumplimiento/cumplimiento';

/**
 * DUMB COMPONENT: DatosProgramacion
 *
 * Panel de solo lectura con los datos de la programación.
 *
 * Legacy: CreateOrUpdateCumplimientoViewImpl.ui.xml líneas 63-155
 * Orden de campos:
 *   1. Orientación | Tipo
 *   2. Aseguradora | Cliente
 *   3. Servicio | Tarea
 *   4. Fecha Programación | Hora Inicial
 *   5. Duración | Cantidad Programada (horas, unidades ó puntos)
 */
@Component({
  selector: 'app-datos-programacion',
  standalone: true,
  imports: [CommonModule, FieldsetModule],
  providers: [DatePipe],
  template: `
    <p-fieldset
      legend="Datos Programación"
      [toggleable]="true"
      styleClass="mb-4"
    >
      @if (programacion(); as prog) {
        <div
          class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm"
        >
          <!-- Fila 1: Orientación | Tipo -->
          <!-- Legacy: isCumpComplementarios → slDescOrden.setVisible(false), slDescAseguradora.setVisible(false), slCliente.setVisible(false) -->
          @if (!ocultarCamposComplementarios()) {
            <div>
              <span class="text-gray-500">Orientación:</span>
              <span class="ml-2 font-medium">{{
                prog.nmconsecutivoManualOrden || '-'
              }}</span>
            </div>
          }
          <div>
            <span class="text-gray-500">Tipo:</span>
            <span class="ml-2 font-medium">{{ prog.dstipoCita || '-' }}</span>
          </div>

          <!-- Fila 2: Aseguradora | Cliente -->
          @if (!ocultarCamposComplementarios()) {
            <div>
              <span class="text-gray-500">Aseguradora:</span>
              <span class="ml-2 font-medium">{{
                prog.dsnombreAseguradora || '-'
              }}</span>
            </div>
          }
          <div>
            <span class="text-gray-500">Cliente:</span>
            <span class="ml-2 font-medium">{{
              prog.dsnombreCliente || prog.dsnombreBeneficiario || '-'
            }}</span>
          </div>

          <!-- Fila 3: Servicio | Tarea -->
          <div>
            <span class="text-gray-500">Servicio:</span>
            <span class="ml-2 font-medium">{{
              prog.dsnombreServicio || '-'
            }}</span>
          </div>
          <div>
            <span class="text-gray-500">Tarea:</span>
            <span class="ml-2 font-medium">{{ prog.dstarea || '-' }}</span>
          </div>

          <!-- Fila 4: Fecha de Programación | Hora Inicial de Programación -->
          <div>
            <span class="text-gray-500">Fecha de Programación:</span>
            <span class="ml-2 font-medium">{{
              formatFecha(prog.feprogramacion)
            }}</span>
          </div>
          <div>
            <span class="text-gray-500">Hora Inicial de Programación:</span>
            <span class="ml-2 font-medium">{{
              formatHora(prog.horaInicial)
            }}</span>
          </div>

          <!-- Fila 5: Duración | Cantidad Programada (horas, unidades ó puntos) -->
          <div>
            <span class="text-gray-500">Duración:</span>
            <span class="ml-2 font-medium">{{ prog.nmhorasCita || '-' }}</span>
          </div>
          <div>
            <span class="text-gray-500"
              >Cantidad Programada (horas, unidades ó puntos):</span
            >
            <span class="ml-2 font-medium">{{
              prog.nmcantidadProgramada || '-'
            }}</span>
          </div>
        </div>
      }
    </p-fieldset>
  `
})
export class DatosProgramacionComponent {
  private datePipe = new DatePipe('es-CO');

  // === Inputs ===
  programacion = input<ProgramacionPendiente | null>(null);
  /** Legacy: isCumpComplementarios → oculta Orientación, Aseguradora */
  ocultarCamposComplementarios = input<boolean>(false);

  /**
   * Formatea fecha a DD/MM/YYYY (formato legacy)
   * Legacy: FilterOrdenesProgViewImpl.java muestra fecha como dd/MM/yyyy
   */
  formatFecha(fecha: string | Date | null | undefined): string {
    if (!fecha) return '-';
    try {
      let dateObj: Date;
      if (typeof fecha === 'string') {
        // Si viene como YYYY-MM-DD o YYYY-MM-DDTHH:mm:ss, extraer solo la parte de fecha
        const datePart = fecha.split('T')[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
          const [year, month, day] = datePart.split('-').map(Number);
          dateObj = new Date(year, month - 1, day);
        } else {
          dateObj = new Date(fecha);
        }
      } else {
        dateObj = fecha;
      }
      // Legacy usa barras: dd/MM/yyyy
      const day = dateObj.getDate().toString().padStart(2, '0');
      const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
      const year = dateObj.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return String(fecha);
    }
  }

  /**
   * Formatea hora a HH:MM (sin segundos, formato legacy)
   * Legacy: CreateOrUpdateCumplimientoViewImpl.java usa "HH:MM"
   */
  formatHora(hora: string | null | undefined): string {
    if (!hora) return '-';
    // Si viene como HH:MM:SS, tomar solo HH:MM
    const parts = hora.split(':');
    if (parts.length >= 2) {
      return `${parts[0]}:${parts[1]}`;
    }
    return hora;
  }
}
